import axiosInstance from "../../api/axiosConfig";
import { getApiBaseUrl } from "../../baseUrl";

export interface DashboardStats {
  totalEmpresas: number;
  vagasAtivas: number;
  totalCandidatos: number;
  totalCandidaturas: number;
}

export interface VagasPorEmpresa {
  nome: string;
  vagas: number;
}

export const buscarEstatisticasDashboard =
  async (): Promise<DashboardStats> => {
    const apiUrl = getApiBaseUrl();

    let totalEmpresas = 0;
    let vagasAtivas = 0;
    let totalCandidatos = 0;
    let totalCandidaturas = 0;

    try {
      // Buscar contagem de empresas
      try {
        const empresasResponse = await axiosInstance.get(
          `${apiUrl}/empresas/count`,
        );
        // Backend pode retornar número direto ou objeto com .data
        totalEmpresas =
          typeof empresasResponse.data === "number"
            ? empresasResponse.data
            : empresasResponse.data?.data || 0;
      } catch (error: any) {
        if (error.code === "ERR_NETWORK" || error.code === "ECONNREFUSED") {
          console.error(
            "❌ Backend indisponível - não é possível buscar contagem de empresas",
          );
        } else {
          console.error("❌ Erro ao buscar contagem de empresas:", error);
        }
        totalEmpresas = 0;
      }

      // Buscar contagem de vagas ativas
      try {
        const vagasResponse = await axiosInstance.get(
          `${apiUrl}/vagas/ativas/count`,
        );
        // Backend pode retornar número direto ou objeto com .data
        vagasAtivas =
          typeof vagasResponse.data === "number"
            ? vagasResponse.data
            : vagasResponse.data?.data || 0;
      } catch (error: any) {
        if (error.code === "ERR_NETWORK" || error.code === "ECONNREFUSED") {
          console.error(
            "❌ Backend indisponível - não é possível buscar contagem de vagas ativas",
          );
        } else {
          console.error("❌ Erro ao buscar contagem de vagas ativas:", error);
        }
        vagasAtivas = 0;
      }

      // Buscar contagem de candidatos
      try {
        const candidatosResponse = await axiosInstance.get(
          `${apiUrl}/candidatos/count`,
        );
        // Backend pode retornar número direto ou objeto com .data
        totalCandidatos =
          typeof candidatosResponse.data === "number"
            ? candidatosResponse.data
            : candidatosResponse.data?.data || 0;
      } catch (error: any) {
        if (error.code === "ERR_NETWORK" || error.code === "ECONNREFUSED") {
          console.error(
            "❌ Backend indisponível - não é possível buscar contagem de candidatos",
          );
        } else {
          console.error("❌ Erro ao buscar contagem de candidatos:", error);
        }
        totalCandidatos = 0;
      }

      // Buscar contagem de candidaturas ativas
      try {
        const candidaturasResponse = await axiosInstance.get(
          `${apiUrl}/candidaturas/ativas/count`,
        );
        // Backend pode retornar número direto ou objeto com .data
        totalCandidaturas =
          typeof candidaturasResponse.data === "number"
            ? candidaturasResponse.data
            : candidaturasResponse.data?.data || 0;
      } catch (error) {
        console.error("Erro ao buscar contagem de candidaturas ativas:", error);
        totalCandidaturas = 0;
      }
    } catch (error) {
      console.error("Erro geral ao buscar estatísticas:", error);
    }

    const resultado = {
      totalEmpresas,
      vagasAtivas,
      totalCandidatos,
      totalCandidaturas,
    };

    return resultado;
  };

export const buscarVagasPorEmpresa = async (): Promise<VagasPorEmpresa[]> => {
  const apiUrl = getApiBaseUrl();

  try {
    const response = await axiosInstance.get(`${apiUrl}/vagas/por-empresa`);

    // Verificar se é um erro do backend (success: false ou message de erro)
    if (
      response.data?.success === false ||
      response.data?.message?.includes("Erro")
    ) {
      throw new Error(response.data.message || "Erro no backend");
    }

    // Backend pode retornar array direto ou objeto com .data
    const data = Array.isArray(response.data)
      ? response.data
      : response.data?.data || [];

    return data;
  } catch (error) {
    // FALLBACK: Buscar todas as vagas e agrupar por empresa no frontend
    try {
      const vagasResponse = await axiosInstance.get(`${apiUrl}/vagas`);
      const todasVagas = Array.isArray(vagasResponse.data)
        ? vagasResponse.data
        : vagasResponse.data?.data || vagasResponse.data?.content || [];

      // Agrupar vagas por empresa
      const vagasPorEmpresa = new Map<
        number,
        { nome: string; count: number }
      >();

      todasVagas.forEach((vaga: any) => {
        const empresaId = vaga.id_empresa?.id;
        const empresaNome = vaga.id_empresa?.nome || `Empresa ${empresaId}`;

        if (empresaId) {
          if (vagasPorEmpresa.has(empresaId)) {
            vagasPorEmpresa.get(empresaId)!.count++;
          } else {
            vagasPorEmpresa.set(empresaId, { nome: empresaNome, count: 1 });
          }
        }
      });

      // Converter Map para array ordenado por quantidade (formato da interface)
      const resultado = Array.from(vagasPorEmpresa.entries())
        .map(([empresaId, data]) => ({
          nome: data.nome,
          vagas: data.count,
        }))
        .sort((a, b) => b.vagas - a.vagas);

      return resultado;
    } catch (fallbackError) {
      console.error("Erro ao buscar vagas por empresa:", fallbackError);
      return [];
    }
  }
};
